import React, { useMemo, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Iter-61 — Reusable checkbox-based US-state selector.
 *
 * Storage contract: canonical 2-letter codes only, matching the
 * backend `US_STATES` allow-list. `null` / `undefined` / `[]` == no
 * restriction. Used everywhere Buyer- or destination-level accepted
 * states are edited: Manage Buyer, Destination override, etc.
 *
 * Backward-compat: `initialValue` may be a JS array of codes, a
 * comma-separated string, or a legacy destination's `state_allowlist`.
 * We normalise everything to an uppercased Set on mount.
 */

// Canonical list mirrors `backend/routes/delivery_routes._US_STATES`.
// Order the array so [Continental US] is trivial to compute below.
export const US_STATES = [
  { code: "AL", name: "Alabama", continental: true },
  { code: "AK", name: "Alaska", continental: false },
  { code: "AZ", name: "Arizona", continental: true },
  { code: "AR", name: "Arkansas", continental: true },
  { code: "CA", name: "California", continental: true },
  { code: "CO", name: "Colorado", continental: true },
  { code: "CT", name: "Connecticut", continental: true },
  { code: "DE", name: "Delaware", continental: true },
  { code: "DC", name: "District of Columbia", continental: true },
  { code: "FL", name: "Florida", continental: true },
  { code: "GA", name: "Georgia", continental: true },
  { code: "HI", name: "Hawaii", continental: false },
  { code: "ID", name: "Idaho", continental: true },
  { code: "IL", name: "Illinois", continental: true },
  { code: "IN", name: "Indiana", continental: true },
  { code: "IA", name: "Iowa", continental: true },
  { code: "KS", name: "Kansas", continental: true },
  { code: "KY", name: "Kentucky", continental: true },
  { code: "LA", name: "Louisiana", continental: true },
  { code: "ME", name: "Maine", continental: true },
  { code: "MD", name: "Maryland", continental: true },
  { code: "MA", name: "Massachusetts", continental: true },
  { code: "MI", name: "Michigan", continental: true },
  { code: "MN", name: "Minnesota", continental: true },
  { code: "MS", name: "Mississippi", continental: true },
  { code: "MO", name: "Missouri", continental: true },
  { code: "MT", name: "Montana", continental: true },
  { code: "NE", name: "Nebraska", continental: true },
  { code: "NV", name: "Nevada", continental: true },
  { code: "NH", name: "New Hampshire", continental: true },
  { code: "NJ", name: "New Jersey", continental: true },
  { code: "NM", name: "New Mexico", continental: true },
  { code: "NY", name: "New York", continental: true },
  { code: "NC", name: "North Carolina", continental: true },
  { code: "ND", name: "North Dakota", continental: true },
  { code: "OH", name: "Ohio", continental: true },
  { code: "OK", name: "Oklahoma", continental: true },
  { code: "OR", name: "Oregon", continental: true },
  { code: "PA", name: "Pennsylvania", continental: true },
  { code: "RI", name: "Rhode Island", continental: true },
  { code: "SC", name: "South Carolina", continental: true },
  { code: "SD", name: "South Dakota", continental: true },
  { code: "TN", name: "Tennessee", continental: true },
  { code: "TX", name: "Texas", continental: true },
  { code: "UT", name: "Utah", continental: true },
  { code: "VT", name: "Vermont", continental: true },
  { code: "VA", name: "Virginia", continental: true },
  { code: "WA", name: "Washington", continental: true },
  { code: "WV", name: "West Virginia", continental: true },
  { code: "WI", name: "Wisconsin", continental: true },
  { code: "WY", name: "Wyoming", continental: true },
  { code: "PR", name: "Puerto Rico", continental: false },
  { code: "VI", name: "U.S. Virgin Islands", continental: false },
  { code: "GU", name: "Guam", continental: false },
];

const VALID_CODES = new Set(US_STATES.map((s) => s.code));

/** Coerce any legacy input (array, comma-separated string, list of
 *  lower-case codes, etc.) into a canonical sorted array of
 *  uppercased codes. */
export function normalizeStateInput(raw) {
  if (raw == null) return [];
  let arr = raw;
  if (typeof arr === "string") {
    arr = arr.split(",");
  }
  if (!Array.isArray(arr)) return [];
  const out = [];
  const seen = new Set();
  for (const item of arr) {
    const code = String(item || "").trim().toUpperCase();
    if (!code) continue;
    if (!VALID_CODES.has(code)) continue;   // ignore garbage silently
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  out.sort();
  return out;
}


export default function StateCheckboxSelector({
  value,                        // string | string[] | null
  onChange,                     // (nextArray) => void
  testId = "state-selector",
  helpText,
  disabled = false,
  restrictTo,                   // optional Set<string> — parent buyer allow-list
}) {
  const selected = useMemo(() => new Set(normalizeStateInput(value)), [value]);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return US_STATES;
    return US_STATES.filter(
      (s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q),
    );
  }, [query]);

  const isAllowed = (code) => (restrictTo ? restrictTo.has(code) : true);

  const emit = (nextSet) => {
    const out = US_STATES.map((s) => s.code).filter((c) => nextSet.has(c));
    onChange(out);
  };

  const toggle = (code) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    emit(next);
  };

  const selectAll = () => {
    if (disabled) return;
    const next = new Set(
      US_STATES.filter((s) => isAllowed(s.code)).map((s) => s.code),
    );
    emit(next);
  };

  const clearAll = () => {
    if (disabled) return;
    emit(new Set());
  };

  const continentalOnly = () => {
    if (disabled) return;
    const next = new Set(
      US_STATES.filter((s) => s.continental && isAllowed(s.code)).map((s) => s.code),
    );
    emit(next);
  };

  const count = selected.size;
  const total = restrictTo ? restrictTo.size : US_STATES.length;

  return (
    <div className="border border-black" data-testid={testId}>
      <div className="border-b border-black bg-zinc-100 px-3 py-2 flex flex-wrap items-center gap-2">
        <div className="text-[10px] uppercase tracking-widest font-bold" data-testid={`${testId}-count`}>
          {count} of {total} states selected
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={selectAll}
            disabled={disabled}
            className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40"
            data-testid={`${testId}-select-all`}
          >
            Select all
          </button>
          <button
            type="button"
            onClick={continentalOnly}
            disabled={disabled}
            className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40"
            data-testid={`${testId}-continental`}
          >
            Continental US
          </button>
          <button
            type="button"
            onClick={clearAll}
            disabled={disabled}
            className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40"
            data-testid={`${testId}-clear`}
          >
            Clear all
          </button>
        </div>
      </div>
      <div className="border-b border-black px-3 py-2 flex items-center gap-2">
        <MagnifyingGlass size={12} weight="bold" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by name or code…"
          className="flex-1 border-none bg-transparent text-xs font-mono outline-none"
          data-testid={`${testId}-search`}
        />
      </div>
      <div
        className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2"
        data-testid={`${testId}-grid`}
      >
        {filtered.map((s) => {
          const checked = selected.has(s.code);
          const allowed = isAllowed(s.code);
          return (
            <label
              key={s.code}
              className={
                "flex items-center gap-2 cursor-pointer select-none min-h-[44px] " +
                (allowed ? "" : "opacity-40 cursor-not-allowed")
              }
              title={
                allowed
                  ? undefined
                  : "This state is not in the buyer's accepted list — cannot override to it."
              }
            >
              <input
                type="checkbox"
                className="w-5 h-5 border border-black accent-black"
                checked={checked}
                disabled={disabled || !allowed}
                onChange={() => toggle(s.code)}
                data-testid={`${testId}-cb-${s.code}`}
              />
              <span className="text-xs leading-tight">
                <span className="font-medium">{s.name}</span>{" "}
                <span className="font-mono text-zinc-500">({s.code})</span>
              </span>
            </label>
          );
        })}
        {!filtered.length && (
          <div className="col-span-full text-xs text-zinc-500 py-4 text-center">
            No states match “{query}”.
          </div>
        )}
      </div>
      {helpText && (
        <div className="border-t border-black px-3 py-2 text-[11px] text-zinc-600 bg-zinc-50 leading-relaxed">
          {helpText}
        </div>
      )}
    </div>
  );
}
